import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Users, Calendar, CreditCard, Bell, Shield, BarChart3, Phone, Globe, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface EssentialPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EssentialPlanModal: React.FC<EssentialPlanModalProps> = ({ isOpen, onClose }) => {
  const essentialFeatures = [
    {
      category: "User Management",
      icon: Users,
      features: [
        "Unlimited users and families",
        "Role-based access control",
        "Parent portal",
        "Staff management system"
      ]
    },
    {
      category: "Scheduling & Classes",
      icon: Calendar,
      features: [
        "Class scheduling and management",
        "Makeup class tracking",
        "Attendance tracking",
        "Automated notifications"
      ]
    },
    {
      category: "Payments & Billing",
      icon: CreditCard,
      features: [
        "Stripe integration for parent billing & invoicing",
        "Automated tuition collection",
        "Payment plan management",
        "Late fee automation",
        "Financial reporting"
      ]
    },
    {
      category: "Communication",
      icon: Bell,
      features: [
        "2-way in-app messaging",
        "Announcement system",
        "Event notifications"
      ]
    },
    {
      category: "Security & Compliance",
      icon: Shield,
      features: [
        "Data encryption via AES-256 and in transit via TLS",
        "COPPA controls",
        "Data backup and recovery",
        "User privacy controls",
        "Secure payment processing"
      ]
    },
    {
      category: "Analytics & Reporting",
      icon: BarChart3,
      features: [
        "Financial reports",
        "Attendance analytics"
      ]
    },
    {
      category: "Support & Integration",
      icon: Phone,
      features: [
        "Live chat assistance",
        "Video training sessions",
        "Data migration support"
      ]
    },
    {
      category: "Mobile & Web Access",
      icon: Globe,
      features: [
        "Mobile-responsive Parent Portal",
        "Cross-browser compatibility"
      ]
    }
  ];

  const hive360Features = [
    "Embeddable class discovery widget",
    "Custom branding and styling",
    "Cross-domain security",
    "Real-time enrollment tracking",
    "Analytics and conversion insights",
    "White-label integration"
  ];

  // Handle keyboard events
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={(e) => {
              // Ensure click is on backdrop, not bubbled from modal content
              if (e.target === e.currentTarget) {
                onClose();
              }
            }}
            onMouseDown={(e) => {
              // Firefox compatibility: handle mousedown for better cross-browser support
              if (e.target === e.currentTarget) {
                e.preventDefault();
              }
            }}
          />
          
          {/* Modal */}
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => {
              // Also handle clicks on the modal container (Firefox compatibility)
              if (e.target === e.currentTarget) {
                onClose();
              }
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 max-w-5xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b border-gray-200/50 bg-gradient-to-r from-blue-50/50 to-blue-100/50">
                <div className="flex items-center justify-between">
                  <div className="text-center flex-1">
                    <h2 className="text-2xl font-bold text-gray-900">Hivetivity Essential Plan</h2>
                    <p className="text-gray-600 mt-1">Everything you need to run your program efficiently</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    className="h-8 w-8 p-0 rounded-full hover:bg-gray-100"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                <div className="grid md:grid-cols-2 gap-6">
                  {essentialFeatures.map((category, index) => (
                    <motion.div
                      key={category.category}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05, duration: 0.3 }}
                      className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-gray-200/50 hover:shadow-md transition-all duration-200"
                    >
                      {/* Category Header */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <category.icon className="w-5 h-5 text-blue-600" />
                        </div>
                        <h3 className="font-semibold text-gray-900">{category.category}</h3>
                      </div>

                      {/* Features List */}
                      <ul className="space-y-2">
                        {category.features.map((feature, featureIndex) => (
                          <motion.li
                            key={featureIndex}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: (index * 0.05) + (featureIndex * 0.02), duration: 0.2 }}
                            className="flex items-start gap-2 text-sm text-gray-700"
                          >
                            <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </motion.li>
                        ))}
                      </ul>
                    </motion.div>
                  ))}

                  {/* Hive360 Section */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: essentialFeatures.length * 0.05, duration: 0.3 }}
                    className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-4 border border-purple-200/50 hover:shadow-md transition-all duration-200 md:col-span-2"
                  >
                    {/* Hive360 Header */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                        <Zap className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">Hive360</h3>
                        <Badge variant="secondary" className="bg-purple-100 text-purple-700 text-xs">
                          Coming July 2025
                        </Badge>
                      </div>
                    </div>

                    {/* Hive360 Features */}
                    <div className="grid md:grid-cols-2 gap-2">
                      {hive360Features.map((feature, featureIndex) => (
                        <motion.div
                          key={featureIndex}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: (essentialFeatures.length * 0.05) + (featureIndex * 0.02), duration: 0.2 }}
                          className="flex items-start gap-2 text-sm text-gray-700"
                        >
                          <Check className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                </div>

                {/* Bottom CTA */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="mt-8 p-6 bg-gradient-to-r from-blue-50/50 to-blue-100/50 rounded-xl border border-gray-200/50"
                >
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Start Your 14-Day Free Trial Today
                    </h3>
                    <p className="text-gray-600 mb-4">
                      No credit card required. Full access to all Essential Plan features.
                    </p>
                    <Button
                      onClick={onClose}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2"
                    >
                      Continue Signup
                    </Button>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EssentialPlanModal;